import React, { useEffect, useState, useRef } from "react";
import toast from "react-hot-toast";
import { apiCall } from "../../../utils/apiClient";
import {
  AddServiceModalProps,
  ServiceType,
  Room,
  Service,
  Addon,
  ImageUpload,
  AddServicePayload,
} from "./types/service-modal.types";

const AddServiceModal: React.FC<AddServiceModalProps> = ({
  onClose,
  bookingId,
  userId,
  onSubmit,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [servicesLoading, setServicesLoading] = useState(true);

  // Data
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [serviceDetails, setServiceDetails] = useState<Service | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [images, setImages] = useState<ImageUpload[]>([]);

  // Form selections
  const [selectedServiceTypeId, setSelectedServiceTypeId] = useState("");
  const [selectedRoomId, setSelectedRoomId] = useState("");
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [selectedAddons, setSelectedAddons] = useState<Record<string, number>>({});

  // Quantity fields
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [quantity, setQuantity] = useState("1");

  // Fetch initial data
  useEffect(() => {
    fetchServiceTypes();
    fetchRooms();
  }, []);

  useEffect(() => {
    if (selectedServiceId) {
      fetchServiceDetails(selectedServiceId);
    } else {
      setServiceDetails(null);
    }
  }, [selectedServiceId]);

  useEffect(() => {
    if (serviceDetails?.addons && width && height) {
      const area = parseFloat(width) * parseFloat(height);
      const updatedAddons: Record<string, number> = {};

      serviceDetails.addons.forEach((addon) => {
        const isRequired = addon.is_required === "1";
        const baseQty = addon.qty || 1;
        const calculatedQty =
          addon.price_type === "square_feet"
            ? Math.max(1, Math.ceil((baseQty / 100) * area))
            : baseQty;

        if (isRequired || selectedAddons.hasOwnProperty(addon.id)) {
          updatedAddons[addon.id] = calculatedQty;
        }
      });

      setSelectedAddons(updatedAddons);
    }
  }, [serviceDetails, width, height]);

  useEffect(() => {
    if (selectedServiceTypeId) {
      fetchRooms();
    }
  }, [selectedServiceTypeId]);

  const fetchServiceTypes = async () => {
    try {
      const result = await apiCall({ endpoint: "services-type", method: "GET" });
      if (result.status === 200) {
        const activeTypes = result.data.filter((type: ServiceType) => type.status === "1");
        setServiceTypes(activeTypes);
      }
    } catch (error) {
      console.error("Error fetching service types:", error);
      toast.error("Failed to load service types");
    } finally {
      setServicesLoading(false);
    }
  };

  const fetchServiceDetails = async (serviceId: string) => {
    try {
      const result = await apiCall({ endpoint: `services/${serviceId}`, method: "GET" });
      if (result.status === 200) {
        setServiceDetails(result.data);
      }
    } catch (error) {
      console.error("Error fetching service details:", error);
      toast.error("Failed to load service details");
    }
  };

  const fetchServicesByType = async (typeId: string) => {
    if (!typeId) {
      setServices([]);
      return;
    }

    setServicesLoading(true);
    try {
      const result = await apiCall({ endpoint: "services", method: "GET" });
      if (result.status === 200) {
        const filtered = result.data.filter(
          (service: Service) => service.service_type_id === typeId && service.status === "1"
        );
        setServices(filtered);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
      toast.error("Failed to load services");
    } finally {
      setServicesLoading(false);
    }
  };

  const fetchRooms = async () => {
    const res = await apiCall({
      endpoint: `rooms?serviceType=${selectedServiceTypeId}`,
      method: "GET",
    });
    if (res.status === 200) {
      setRooms(res.data || []);
    }
  };

  const selectedService = serviceDetails || services.find((s) => s.id === selectedServiceId);
  const isSquareFeet = selectedService?.rate_type === "square_feet";

  const handleAddonChange = (addonId: string, isRequired: boolean, qty: number) => {
    setSelectedAddons((prev) => {
      if (prev[addonId]) {
        const copy = { ...prev };
        if (!isRequired) delete copy[addonId];
        return copy;
      }
      return { ...prev, [addonId]: Math.max(1, qty) };
    });
  };

  const updateAddonQty = (addonId: string, delta: number) => {
    setSelectedAddons((prev) => ({
      ...prev,
      [addonId]: Math.max(1, (prev[addonId] || 1) + delta),
    }));
  };

  const baseTotal = selectedService
    ? isSquareFeet
      ? parseFloat(width || "0") * parseFloat(height || "0") * parseFloat(selectedService.rate || "0")
      : parseFloat(quantity || "0") * parseFloat(selectedService.rate || "0")
    : 0;

  const addonTotal = selectedService?.addons
    ? Object.entries(selectedAddons).reduce((sum, [addonId, qty]) => {
        const addon = selectedService.addons!.find((a) => a.id === addonId);
        return addon ? sum + qty * parseFloat(addon.price || "0") : sum;
      }, 0)
    : 0;

  const subtotal = baseTotal + addonTotal;
  const cgst = 0;
  const sgst = 0;
  const cgstAmount = (subtotal * cgst) / 100;
  const sgstAmount = (subtotal * sgst) / 100;
  const total = subtotal + cgstAmount + sgstAmount;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newImages: ImageUpload[] = files.map((file) => ({
      id: URL.createObjectURL(file),
      file: file,
    }));
    setImages((prev) => [...prev, ...newImages]);
  };

  const removeImage = (id: string) => {
    setImages((prev) => prev.filter((img) => img.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedServiceTypeId) {
      toast.error("Please select a service type");
      return;
    }
    if (!selectedServiceId) {
      toast.error("Please select a service");
      return;
    }
    if (!selectedRoomId) {
      toast.error("Please select a room");
      return;
    }
    if (isSquareFeet && (!width || !height || parseFloat(width) <= 0 || parseFloat(height) <= 0)) {
      toast.error("Please enter valid width and height");
      return;
    }
    if (!isSquareFeet && (!quantity || parseFloat(quantity) <= 0)) {
      toast.error("Please enter valid quantity");
      return;
    }
    if (total <= 0) {
      toast.error("Invalid amount calculated");
      return;
    }

    setLoading(true);

    try {
      let referenceImagesJson: string | undefined;
      if (images.length > 0) {
        const formData = new FormData();
        images.forEach((img) => formData.append("images[]", img.file));

        const uploadRes = await apiCall({
          endpoint: "booking/upload-images",
          method: "POST",
          data: formData,
        });

        referenceImagesJson = JSON.stringify(uploadRes.data.images);
      }

      const payload: AddServicePayload = {
        booking_id: bookingId,
        user_id: userId,
        service_id: selectedServiceId,
        room_id: selectedRoomId,
        service_type_id: selectedServiceTypeId,
        quantity: isSquareFeet
          ? parseFloat(width) * parseFloat(height)
          : parseFloat(quantity),
        unit: selectedService!.rate_type,
        rate: parseFloat(selectedService!.rate),
        amount: baseTotal.toFixed(2),
        room_length: isSquareFeet ? parseFloat(width) : null,
        room_width: isSquareFeet ? parseFloat(height) : null,
        description: null,
        is_additional: "1",
        cgst,
        sgst,
        cgst_amount: cgstAmount.toFixed(2),
        sgst_amount: sgstAmount.toFixed(2),
        ...(referenceImagesJson && { reference_image: referenceImagesJson }),
      };

      if (Object.keys(selectedAddons).length > 0 && selectedService?.addons) {
        const addonsData = selectedService.addons
          .filter((addon) => selectedAddons[addon.id])
          .map((addon) => ({
            addon_id: addon.id,
            addon_name: addon.name,
            quantity: selectedAddons[addon.id],
            unit: addon.price_type || selectedService.rate_type,
            rate: parseFloat(addon.price),
            amount: (parseFloat(addon.price) * selectedAddons[addon.id]).toFixed(2),
            addon_description: addon.description,
            is_required: addon.is_required,
            group_name: addon.group_name,
          }));

        payload.addons = addonsData;
      }

      const result = await apiCall({
        endpoint: "booking/add-additional-services",
        method: "POST",
        data: payload,
      });

      if (result && (result.status === 200 || result.status === 201 || result.success === true)) {
        toast.success("Additional service added successfully!");
        onClose();
        onSubmit();
      } else {
        toast.error(result?.message || "Failed to add service");
      }
    } catch (err) {
      console.error("Error adding service:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg w-full max-w-2xl max-h-[95vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b flex justify-between items-center">
          <h2 className="text-base font-bold text-gray-800">➕ Add Service</h2>
          <button 
            type="button"
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700 text-lg" 
            disabled={loading}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {servicesLoading && !serviceTypes.length ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <>
              {/* Service Type & Room */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block mb-1 text-xs font-semibold text-gray-700">
                    Service Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full px-2 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                    value={selectedServiceTypeId}
                    onChange={(e) => {
                      setSelectedServiceTypeId(e.target.value);
                      setSelectedServiceId("");
                      setSelectedAddons({});
                      fetchServicesByType(e.target.value);
                    }}
                    required
                  >
                    <option value="">Select Type</option>
                    {serviceTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 text-xs font-semibold text-gray-700">
                    Room <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full px-2 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                    value={selectedRoomId}
                    onChange={(e) => setSelectedRoomId(e.target.value)}
                    required
                  >
                    <option value="">Select Room</option>
                    {rooms.map((room) => (
                      <option key={room.id} value={room.id}>
                        {room.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Service Selection */}
              {selectedServiceTypeId && (
                <div>
                  <label className="block mb-1 text-xs font-semibold text-gray-700">
                    Service <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full px-2 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                    value={selectedServiceId}
                    onChange={(e) => {
                      setSelectedServiceId(e.target.value);
                      setSelectedAddons({});
                      setWidth("");
                      setHeight("");
                      setQuantity("1");
                    }}
                    required
                  >
                    <option value="">Select Service</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.name} - ₹{service.rate}/{service.rate_type?.replace("_", " ")}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Service Details */}
              {selectedService && (
                <>
                  {/* Quantity/Dimensions */}
                  {isSquareFeet ? (
                    <div>
                      <label className="block mb-1 text-xs font-semibold text-gray-700">
                        Dimensions (ft) <span className="text-red-500">*</span>
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="number"
                          step="0.01"
                          placeholder="Width"
                          className="border border-gray-300 rounded px-2 py-2 text-sm focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                          value={width}
                          onChange={(e) => setWidth(e.target.value)}
                          required
                        />
                        <input
                          type="number"
                          step="0.01"
                          placeholder="Height"
                          className="border border-gray-300 rounded px-2 py-2 text-sm focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                          value={height}
                          onChange={(e) => setHeight(e.target.value)}
                          required
                        />
                      </div>
                      {width && height && (
                        <div className="text-xs text-gray-600 mt-1">
                          Area: <span className="font-semibold">{(parseFloat(width) * parseFloat(height)).toFixed(2)} sq ft</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <label className="block mb-1 text-xs font-semibold text-gray-700">
                        {selectedService.rate_type?.replace("_", " ").toUpperCase()} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Quantity"
                        className="border border-gray-300 rounded px-2 py-2 w-full text-sm focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        required
                      />
                    </div>
                  )}

                  {/* Addons */}
                  {selectedService?.addons && selectedService.addons.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-xs text-gray-800 mb-2">Add-ons</h3>
                      {selectedService.addons
                        .reduce((acc: { name: string; addons: Addon[] }[], addon) => {
                          const group = acc.find((g) => g.name === addon.group_name);
                          if (group) {
                            group.addons.push(addon);
                          } else {
                            acc.push({ name: addon.group_name, addons: [addon] });
                          }
                          return acc;
                        }, [])
                        .map((group) => (
                          <div key={group.name} className="mb-2 border border-gray-200 rounded">
                            <div className="px-2 py-1.5 bg-gray-50 font-semibold text-xs text-blue-600">
                              {group.name}
                            </div>
                            <div className="bg-white">
                              {group.addons.map((addon) => {
                                const isRequired = addon.is_required === "1";
                                const isChecked = selectedAddons.hasOwnProperty(addon.id);
                                const baseQty = addon.qty || 1;
                                const calculatedQty =
                                  addon.price_type === "square_feet" && width && height
                                    ? Math.max(1, Math.ceil((baseQty / 100) * (parseFloat(width) * parseFloat(height))))
                                    : 1;
                                const addonQty = isChecked ? selectedAddons[addon.id] : calculatedQty;

                                return (
                                  <label
                                    key={addon.id}
                                    className="flex justify-between items-start px-2 py-1.5 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 text-xs"
                                  >
                                    <div className="flex items-start flex-1">
                                      <input
                                        type="checkbox"
                                        className="mt-0.5 mr-2 h-3 w-3"
                                        checked={isRequired || isChecked}
                                        onChange={() => handleAddonChange(addon.id, isRequired, addonQty)}
                                        disabled={isRequired}
                                      />
                                      <div className="flex-1">
                                        <div className="font-semibold text-gray-800">{addon.name}</div>
                                        {addon.description && <div className="text-gray-500 text-[10px] mt-0.5">{addon.description}</div>}
                                        <div className="text-[10px] mt-1 flex items-center gap-1 flex-wrap">
                                          {addon.price_type === "unit" && isChecked ? (
                                            <>
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  updateAddonQty(addon.id, -1);
                                                }}
                                                className="bg-gray-200 hover:bg-gray-300 px-1.5 py-0.5 rounded"
                                              >
                                                −
                                              </button>
                                              <span className="font-semibold">{addonQty}</span>
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  updateAddonQty(addon.id, 1);
                                                }}
                                                className="bg-gray-200 hover:bg-gray-300 px-1.5 py-0.5 rounded"
                                              >
                                                +
                                              </button>
                                              <span className="ml-1 text-gray-600">| ₹{addon.price}</span>
                                            </>
                                          ) : (
                                            <span className="text-gray-600">
                                              Qty: {addonQty} | ₹{addon.price}
                                            </span>
                                          )}
                                          {isRequired && <span className="ml-1 text-red-500 font-medium">(Req)</span>}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="text-xs font-semibold text-gray-700 ml-2">
                                      ₹{(addonQty * parseFloat(addon.price || "0")).toFixed(2)}
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Price Summary */}
                  <div className="bg-gray-50 rounded p-2 space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span>Base:</span>
                      <span className="font-semibold">₹{baseTotal.toFixed(2)}</span>
                    </div>
                    {addonTotal > 0 && (
                      <div className="flex justify-between">
                        <span>Add-ons:</span>
                        <span className="font-semibold">₹{addonTotal.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-1 border-t">
                      <span>Subtotal:</span>
                      <span className="font-semibold">₹{subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>CGST (9%):</span>
                      <span>₹{cgstAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>SGST (9%):</span>
                      <span>₹{sgstAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between pt-1 border-t font-bold text-indigo-600 text-sm">
                      <span>Total:</span>
                      <span>₹{total.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Image Upload */}
                  <div>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-3 rounded text-xs flex items-center justify-center"
                    >
                      📷 Upload Reference Images
                    </button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      multiple
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </div>

                  {/* Image Preview */}
                  {images.length > 0 && (
                    <div className="grid grid-cols-4 gap-2">
                      {images.map((img) => (
                        <div key={img.id} className="relative group">
                          <img
                            src={img.id}
                            alt="Reference"
                            className="w-full h-16 object-cover rounded border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(img.id)}
                            className="absolute top-0.5 right-0.5 h-4 w-4 bg-red-600 hover:bg-red-700 text-white rounded-full text-[10px] flex items-center justify-center"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="border border-gray-300 px-4 py-1.5 rounded text-sm hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !selectedServiceId || !selectedRoomId}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-1.5 rounded text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Adding...
              </>
            ) : (
              "Add Service"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddServiceModal;
